import type { IScope } from "angular";
import { cloneDeep, isEmpty } from "lodash";
import { finalize } from "rxjs/operators";

import type { IAlarmCentral, IPartner, IResponseWithLoading } from "App/Modules/Models";
import type { IAlarmCentralsRepository, IPartnersRepository } from "App/Modules/Repositories";
import type { IFormContainerResolve } from "App/Modules/Services";
import { PatchDelta } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";
import type { IConfig } from "App/Modules/Utils";

export default class EditPartnerDetailsComponentController {
    public static $inject: string[] = [
        "$scope",
        "mmPartnersRepository",
        "mmAlarmCentralsRepository",
        "irConfig",
        "Upload",
    ];

    public original: IPartner;

    public edited: IPartner;

    public resolve: IFormContainerResolve<IPartner, void>;

    public alarmCentrals: IResponseWithLoading<IAlarmCentral[]>;

    public editResponse: IResponseWithLoading<IPartner>;

    public deletePartnerLogoResponse: IResponseWithLoading<void>;

    public isUploadingImage: boolean;

    constructor(
        private readonly $scope: IScope,
        private readonly partnersRepository: IPartnersRepository,
        private readonly alarmCentralRepository: IAlarmCentralsRepository,
        private readonly config: IConfig,
        private readonly Upload: any,
    ) {}

    public $onInit() {
        this.original = this.resolve.formData;
        this.edited = cloneDeep(this.original);

        extendWithLoadingAndFail(this.alarmCentralRepository.getAlarmCentrals())
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    this.alarmCentrals = response;
                },
                (error) => {
                    this.alarmCentrals = error;
                },
            );
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public editPartnerDetails() {
        if (!this.edited.useIrisCortex) {
            this.edited.defaultCortexArc = null;
        }

        const patchObject = PatchDelta.difference(this.original, this.edited);

        if (!isEmpty(patchObject)) {
            extendWithLoadingAndFail(this.partnersRepository.patch(this.original.id, patchObject))
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (response) => {
                        this.editResponse = response;

                        if (!response.isLoading && response.data) {
                            this.resolve.options.onOk(response.data);
                        }
                    },
                    (error) => {
                        this.editResponse = error;
                    },
                );
        } else {
            this.cancel();
        }
    }

    public uploadImage(file: any) {
        if (file) {
            this.isUploadingImage = true;

            file.upload = this.Upload.upload({
                data: { file },
                method: "PUT",
                url: `${this.config.apiUrl}partners/${this.original.id}/logo`,
            });

            file.upload.then(
                (response: any) => {
                    const partner = cloneDeep(this.original);
                    partner.logo = response.data;
                    partner.logo.url += `?${Date.now()}`; // Logo will reload if URL is updated

                    this.resolve.options.onOk(partner);
                },
                () => {
                    this.isUploadingImage = false;
                },
            );
        }
    }

    public deletePartnerLogo() {
        extendWithLoadingAndFail(this.partnersRepository.deletePartnerLogo(this.original.id))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    this.deletePartnerLogoResponse = response;
                    if (!response.isLoading) {
                        const partner = cloneDeep(this.original);
                        partner.logo = null;
                        this.resolve.options.onOk(partner);
                    }
                },
                (error) => {
                    this.deletePartnerLogoResponse = error;
                },
            );
    }
}
